import React, { useMemo } from 'react';
import hexToHSL from '../../helpers/hexToHSL';
import getNumberFormatted from '../../helpers/getNumberFormatted';

const NumericProgressBar = ({
                                minValue = 0,
                                maxValue,
                                targetValues,
                                title = '',
                                description = '',
                                height = 24,
                                backgroundColor = '#F0F0F0',
                                baseColor = '#BDCCDE'
                            }) => {
    const { segments, context } = useMemo(() => {
        // Ensure targetValues is an array
        const targets = Array.isArray(targetValues) ? targetValues : [targetValues];

        // Sort targets to render them in order
        const sortedTargets = [...targets].sort((a, b) => a - b);

        const range = maxValue - minValue;
        const segments = [];
        let previousValue = minValue;

        // Create context for mustache templates
        const context = {};
        sortedTargets.forEach((value, index) => {
            context[`t${index + 1}`] = getNumberFormatted(value);
        });

        // Generate segments with different shades
        sortedTargets.forEach((target, index) => {
            const percentage = ((target - minValue) / range) * 100;
            const startPercentage = ((previousValue - minValue) / range) * 100;

            // Convert hex to HSL and adjust lightness
            const hslColor = hexToHSL(baseColor);
            const adjustedLightness = Math.max(20, hslColor.l - (index * 15));
            const segmentColor = `hsl(${hslColor.h}, ${hslColor.s}%, ${adjustedLightness}%)`;

            segments.push({
                value: target,
                startPercentage,
                endPercentage: percentage,
                width: percentage - startPercentage,
                color: segmentColor
            });

            previousValue = target;
        });

        return { segments, context };
    }, [minValue, maxValue, targetValues, baseColor]);

    // Process description with context values
    const processedDescription = description.replace(/\{([^}]+)\}/g, (match, key) => {
        return context[key] !== undefined ? context[key] : match;
    });

    return (
        <div className="ProgressChartBar">
            {title || processedDescription ? <div className="ProgressChartBar__header">
                {title && (
                    <div className="ProgressChartBar__title">
                        {title}
                    </div>
                )}
                {processedDescription && (
                    <div className="ProgressChartBar__description">
                        {processedDescription}
                    </div>
                )}
            </div> : null}
            <div className="ProgressChartBar__bar">
                {segments.map((segment, index) => (
                    <div
                        key={index}
                        className="ProgressChartBar__segment"
                        style={{
                            left: `${segment.startPercentage}%`,
                            width: `${segment.width}%`,
                            backgroundColor: segment.color
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default NumericProgressBar;